// --- Dashboard.js (Ausschnitt/Drop-in) ---
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getLogs } from "../utils/api";
import androidPng from "../assets/android.png";
import iosPng from "../assets/ios.png";
import pcPng from "../assets/pc.png";

const makeIcon = (url) => new L.Icon({ iconUrl: url, iconSize: [25, 25], iconAnchor: [12, 25], popupAnchor: [0, -20] });
const iosIcon = makeIcon(iosPng);
const androidIcon = makeIcon(androidPng);
const defaultIcon = makeIcon(pcPng);
const toNum = (v) => (v === null || v === undefined ? null : Number(v));

export default function Dashboard() {
  const [scans, setScans] = useState([]);
  const [stats, setStats] = useState({ total: 0, devices: 0, qrcodes: 0, ips: 0 });
  const [loading, setLoading] = useState(false);
  const [qrSel, setQrSel] = useState("");
  const [justRefreshed, setJustRefreshed] = useState(false); // << neu

  async function load() {
    setLoading(true);
    try {
      const data = await getLogs(120);
      const norm = (data || []).map((s) => {
        const ua = (s.userAgent || "").toLowerCase();
        const os = s.os || (ua.includes("android") ? "Android" : ua.match(/iphone|ipad|mac|ios|apple/) ? "Apple" : "Unknown");
        const device = s.device || (os === "Android" ? "Android" : os === "Apple" ? "iOS" : "Desktop");
        return {
          ...s,
          os, device,
          scanId: s.scanId || s.scandId || s.qrCode || s.key || "unknown",
          city: s.city || s.geo?.city || "",
          region: s.region || s.geo?.region || "",
          country: s.country || s.geo?.country || "",
          lat: s.lat ?? s.latitude ?? s.geo?.lat ?? null,
          lon: s.lon ?? s.lng ?? s.longitude ?? s.geo?.lon ?? null,
        };
      });
      setScans(norm);

      const osSet = new Set(norm.map((x) => x.os).filter(Boolean));
      const qrSet = new Set(norm.map((x) => x.scanId).filter(Boolean));
      const ipSet = new Set(norm.map((x) => x.ip).filter(Boolean));
      setStats({ total: norm.length, devices: osSet.size, qrcodes: qrSet.size, ips: ipSet.size });

      // Flash für Button
      setJustRefreshed(true);
      setTimeout(() => setJustRefreshed(false), 800);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

  const qrCounts = useMemo(() => {
    const m = new Map();
    scans.forEach(s => m.set(s.scanId || "unknown", (m.get(s.scanId || "unknown") || 0) + 1));
    return [...m.entries()].map(([code, count]) => ({ code, count })).sort((a,b)=>a.code.localeCompare(b.code));
  }, [scans]);

  const filtered = useMemo(() => (!qrSel ? scans : scans.filter((s) => s.scanId === qrSel)), [scans, qrSel]);

  const points = useMemo(() => {
    return filtered
      .map((s) => {
        const lat = toNum(s.lat), lon = toNum(s.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
        const icon =
          String(s.os).toLowerCase().includes("android")
            ? androidIcon
            : String(s.os).toLowerCase().match(/ios|iphone|apple/)
            ? iosIcon
            : defaultIcon;
        const location = [s.city, s.region, s.country].filter(Boolean).join(", ") || "?";
        return { lat, lon, icon, s, location };
      })
      .filter(Boolean);
  }, [filtered]);

  return (
    <div className="page">
      <h2>📊 Leylix QR-Code Dashboard</h2>

      {/* KPI-Karten */}
      <div className="cards">
        <div className="card">Gesamt Scans: {stats.total}</div>
        <div className="card">Geräte/OS: {stats.devices}</div>
        <div className="card">QR Codes: {stats.qrcodes}</div>
        <div className="card">IPs: {stats.ips}</div>
      </div>

      {/* Filterzeile als schöne Karte */}
      <div className="filter-card" style={{ marginTop: 10, marginBottom: 10 }}>
        <button className={`btn btn--primary ${justRefreshed ? "btn--flash" : ""}`} onClick={load} disabled={loading}>
          {loading ? "Aktualisiere…" : "Aktualisieren"}
        </button>
        <select className="select" value={qrSel} onChange={(e) => setQrSel(e.target.value)}>
          <option value="">Alle QR-Codes</option>
          {qrCounts.map(({ code, count }) => (<option key={code} value={code}>{code} ({count})</option>))}
        </select>
      </div>

      {/* Tabelle + Mini-Map (Mini-Map auf Mobile ausblenden) */}
      <div className="content-row" style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 16, marginTop: 8 }}>
        {/* Tabelle */}
        <div className="table-section">
          <h3>Letzte Scans</h3>
          <div className="responsive-table">
            <table className="table">
              <thead>
                <tr>
                  <th>Gerät</th><th>QR Code</th><th className="hide-sm">OS</th><th>Ort</th><th className="hide-sm">Zeit</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={i}>
                    <td data-label="Gerät">{s.device}</td>
                    <td data-label="QR Code">{s.scanId}</td>
                    <td data-label="OS" className="hide-sm">{s.os}</td>
                    <td data-label="Ort">{[s.city, s.country].filter(Boolean).join(", ") || "?"}</td>
                    <td data-label="Zeit" className="hide-sm">{s.timestamp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mini-Map – auf Mobile ausblenden */}
        <div className="map-section mini-map hide-on-mobile">
          <h3>Scan-Standorte</h3>
          <div className="map-wrap" style={{ height: "35vh" }}>
            <MapContainer center={[51.1657, 10.4515]} zoom={5} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {points.map((p, i) => (
                <Marker key={i} position={[p.lat, p.lon]} icon={p.icon}>
                  <Popup>
                    <b>{p.s.device}</b> ({p.s.os})<br />
                    QR: {p.s.scanId}<br />
                    {p.location}<br />
                    {p.s.timestamp}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* Hübsche QR-Code Liste unten (Code | Count rechtsbündig) */}
      <div style={{ marginTop: 16 }}>
        <h3>QR-Codes (Anzahl Scans)</h3>
        <ul className="list list--divided">
          {qrCounts.map(({ code, count }) => (
            <li key={code}>
              <span className="code">{code}</span>
              <span className="count">{count}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
